import { writeFileSync } from 'node:fs'
import { resolve } from 'node:path'
import { api, formatError } from '../api-client'
import { analyticsEvent, flushAndExit } from '../analytics'
import { parseArgs } from './context/args'
import { emitJSON } from './context/render'
import { levenshtein } from './context/flags'
import { resolveWorkspaceSlug } from './context'

// Export CLI — dump the full workspace bundle (owner/admin) to a JSON file.
// Plain `export` writes the bundle to disk; --json streams it to stdout instead.

const KNOWN_EXPORT_FLAGS: ReadonlySet<string> = new Set(['out', 'json', 'help'])

export function assertKnownExportFlags(
  flags: Record<string, string | boolean>,
  onError: (msg: string) => never = (msg) => { console.error(msg); process.exit(1) },
): void {
  const unknown = Object.keys(flags).filter((f) => !KNOWN_EXPORT_FLAGS.has(f))
  if (unknown.length === 0) return
  const lines = unknown.map((f) => {
    let best: string | null = null
    let bestDist = Infinity
    for (const known of KNOWN_EXPORT_FLAGS) {
      const d = levenshtein(f, known)
      if (d < bestDist) { bestDist = d; best = known }
    }
    const hint = best !== null && bestDist <= Math.max(2, Math.floor(f.length / 2)) ? best : null
    return `  --${f}${hint ? `  (did you mean --${hint}?)` : ''}`
  })
  onError(
    `Error: unknown flag${unknown.length > 1 ? 's' : ''} for 'driftless export':\n` +
      lines.join('\n') +
      `\nRun 'driftless export --help' for valid flags.`,
  )
}

// Help lives in ./help-text (light, dep-free) so index.ts can print it without
// importing this command body; re-exported here so EXPORT_HELP still resolves from './export'.
export { EXPORT_HELP } from './help-text'
import { EXPORT_HELP } from './help-text'

function printHelp(stream: NodeJS.WritableStream = process.stdout): void {
  stream.write(EXPORT_HELP)
}

export async function exportCommand(args: string[]): Promise<void> {
  const { flags, positional } = parseArgs(args)
  const isJSON = !!flags['json']

  if (flags['help'] === true || positional[0] === 'help') { printHelp(); process.exit(0) }
  assertKnownExportFlags(flags)

  const ws = await resolveWorkspaceSlug()

  try {
    const bundle = await api.get(`/workspaces/${ws}/export`)
    analyticsEvent('workspace_export', ws, {})
    if (isJSON) { emitJSON(bundle); await flushAndExit(0); return }
    const out = typeof flags['out'] === 'string' && flags['out'].trim()
      ? flags['out'].trim()
      : `driftless-export-${ws}.json`
    const target = resolve(process.cwd(), out)
    writeFileSync(target, JSON.stringify(bundle, null, 2) + '\n')
    console.log(`Exported workspace bundle → ${target}`)
    await flushAndExit(0)
  } catch (e: any) {
    console.error(formatError(e))
    await flushAndExit(1)
  }
}
